import { Component, h } from '@stencil/core';

const COMPONENT_NAME = 'footer-type-5';

/**
 * Full-featured three-section footer with wide navigation, subscribe form, and Schema.org markup.
 * Section 1: form description + subscribe form. Section 2: logo + up to 5 nav menus. Section 3: copyright, legal links, social icons.
 * Most comprehensive footer variant supporting the widest range of content slots.
 *
 * @category Footers
 * @slot logo - {required}, <me-logo> - Company logo with link
 * @slot form-description - {required}, <me-text tag="div"> - Form role description
 * @slot form - {required}, <form-subscribe-type-1> - Short form
 * @slot menus - {required}, {min-items: 2}, {max-items: 5}, <footer-menu-type-1>
 * @slot socials - {required}, <me-socials> - Social media section with heading and icon links
 * @slot copyright - {required}, <me-text variant="small"> - Copyright text
 * @slot legal - {required}, {min-items: 2}, {max-items: 3}, <me-button tag="a" variant="ghost"> - Legal footer links (Privacy Policy, Terms, etc.)
 * @example
 *   <footer-type-5>
 *     <me-logo slot="logo" href="/"><img src="https://via.placeholder.com/120x40" alt="Logo" /></me-logo>
 *     <me-text slot="form-description" tag="div">Get the latest news and product updates.</me-text>
 *     <form-subscribe-type-1 slot="form"></form-subscribe-type-1>
 *     <footer-menu-type-1 slot="menus" title="Product" items='[{"label": "Features", "href": "/features"}, {"label": "Pricing", "href": "/pricing"}, {"label": "Integrations", "href": "/integrations"}]'></footer-menu-type-1>
 *     <footer-menu-type-1 slot="menus" title="Company" items='[{"label": "About", "href": "/about"}, {"label": "Careers", "href": "/careers"}, {"label": "Contact", "href": "/contact"}]'></footer-menu-type-1>
 *     <footer-menu-type-1 slot="menus" title="Resources" items='[{"label": "Blog", "href": "/blog"}, {"label": "Help Center", "href": "/help"}, {"label": "API Docs", "href": "/docs"}]'></footer-menu-type-1>
 *     <me-socials slot="socials" items='[{"type": "linkedin", "href": "https://linkedin.com", "label": "LinkedIn"}, {"type": "x", "href": "https://x.com", "label": "X"}, {"type": "github", "href": "https://github.com", "label": "GitHub"}]'></me-socials>
 *     <me-text slot="copyright" variant="small">&copy; 2025 Company Inc. All rights reserved.</me-text>
 *     <me-button slot="legal" tag="a" variant="ghost" href="/privacy">Privacy Policy</me-button>
 *     <me-button slot="legal" tag="a" variant="ghost" href="/terms">Terms of Service</me-button>
 *   </footer-type-5>
 */
@Component({
  tag: 'footer-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FooterType5 {
  render() {
    return (
      <footer class={COMPONENT_NAME} role="contentinfo" aria-label="Site footer" itemScope itemType="https://schema.org/Organization">
        <me-container yGap="4xl" yGapLg="6xl" xGap="md">
          <me-row
            class={`${COMPONENT_NAME}__form`}
            yGap="4xl"
            yGapLg="2xs"
            direction="column"
            directionLg="row"
            align="stretch"
            alignLg="start"
            justify="start"
            justifyLg="between"
          >
            <me-column span="12" spanLg="6">
              <slot name="form-description"></slot>
            </me-column>
            <me-column span="12" spanLg="6">
              <slot name="form"></slot>
            </me-column>
          </me-row>
          <me-row class={`${COMPONENT_NAME}__secondary`} xGap="lg" xGapLg="4xl" yGap="3xl" yGapLg="2xs" justifyLg="between" direction="column" directionLg="row">
            <me-column span="12" spanMd="2" spanLg="2" class={`${COMPONENT_NAME}__logo`}>
              <div itemProp="logo">
                <slot name="logo"></slot>
              </div>
            </me-column>
            <me-column span="12" spanMd="5" spanLg="10" class={`${COMPONENT_NAME}__menus`}>
              <slot name="menus"></slot>
            </me-column>
          </me-row>
          <me-row
            class={`${COMPONENT_NAME}__row-third`}
            yGap="xl"
            yGapLg="2xl"
            direction="column"
            directionLg="column"
            align="stretch"
            alignLg="stretch"
            justify="start"
            justifyLg="between"
          >
            <me-column>
              <div class={`${COMPONENT_NAME}__divider`} role="separator" aria-hidden="true"></div>
            </me-column>
            <me-column>
              <me-row xGap="lg" xGapLg="xl" yGap="xl" yGapLg="xl" align="start" alignLg="center" direction="column" directionLg="row" justify="start" justifyLg="between">
                <me-column span="12" spanLg="auto" class={`${COMPONENT_NAME}__legal-copyright`}>
                  <me-row xGap="lg" xGapLg="xl" yGap="xl" yGapMd="2xs" yGapLg="2xs" direction="column" directionLg="row" align="start" alignLg="center">
                    <me-column span="12">
                      <slot name="copyright"></slot>
                    </me-column>
                    <me-column span="12" class={`${COMPONENT_NAME}__legal-links`}>
                      <slot name="legal"></slot>
                    </me-column>
                  </me-row>
                </me-column>
                <me-column span="12" spanLg="auto" class={`${COMPONENT_NAME}__socials-language-switcher`}>
                  <div class={`${COMPONENT_NAME}__social-links`} itemProp="sameAs">
                    <slot name="socials"></slot>
                  </div>
                  <div>
                    <slot name="language-switcher"></slot>
                  </div>
                </me-column>
              </me-row>
            </me-column>
          </me-row>
        </me-container>
      </footer>
    );
  }
}
